import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AuditTrail = ({ documentId }) => {
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('30days');

  const auditEvents = [
    {
      id: 1,
      type: 'upload',
      action: 'Document uploaded',
      user: 'John Doe',
      userRole: 'Contract Manager',
      timestamp: '2024-01-15 09:30:00',
      details: 'Master Service Agreement v1.0 uploaded',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome 120.0.0.0'
    },
    {
      id: 2,
      type: 'view',
      action: 'Document viewed',
      user: 'Sarah Wilson',
      userRole: 'Legal Counsel',
      timestamp: '2024-01-15 10:45:00',
      details: 'Viewed document for 15 minutes',
      ipAddress: '192.168.1.105',
      userAgent: 'Firefox 121.0.0.0'
    },
    {
      id: 3,
      type: 'download',
      action: 'Document downloaded',
      user: 'Michael Chen',
      userRole: 'Paralegal',
      timestamp: '2024-01-16 14:20:00',
      details: 'Downloaded PDF copy',
      ipAddress: '192.168.1.110',
      userAgent: 'Safari 17.2.0'
    },
    {
      id: 4,
      type: 'share',
      action: 'Document shared',
      user: 'John Doe',
      userRole: 'Contract Manager',
      timestamp: '2024-01-17 11:15:00',
      details: 'Shared with external party: client@company.com',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome 120.0.0.0'
    },
    {
      id: 5,
      type: 'edit',
      action: 'Document updated',
      user: 'Sarah Wilson',
      userRole: 'Legal Counsel',
      timestamp: '2024-01-18 16:30:00',
      details: 'Updated to version 1.1 - Added payment terms clause',
      ipAddress: '192.168.1.105',
      userAgent: 'Chrome 120.0.0.0'
    },
    {
      id: 6,
      type: 'comment',
      action: 'Comment added',
      user: 'Michael Chen',
      userRole: 'Paralegal',
      timestamp: '2024-01-19 09:45:00',
      details: 'Added review comment on Section 3.2',
      ipAddress: '192.168.1.110',
      userAgent: 'Edge 120.0.0.0'
    }
  ];

  const getEventIcon = (type) => {
    switch (type) {
      case 'upload': return 'Upload';
      case 'view': return 'Eye';
      case 'download': return 'Download';
      case 'share': return 'Share2';
      case 'edit': return 'Edit3';
      case 'comment': return 'MessageCircle';
      case 'delete': return 'Trash2';
      default: return 'Activity';
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'upload': return 'text-success';
      case 'view': return 'text-accent';
      case 'download': return 'text-primary';
      case 'share': return 'text-warning';
      case 'edit': return 'text-secondary';
      case 'comment': return 'text-muted-foreground';
      case 'delete': return 'text-error';
      default: return 'text-foreground';
    }
  };

  const filteredEvents = auditEvents?.filter(event => {
    if (filterType === 'all') return true;
    return event.type === filterType;
  });

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date?.toLocaleDateString(),
      time: date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Document Audit Trail</h3>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export Log
          </Button>
        </div>
        
        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-foreground">Filter by:</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="px-3 py-1 border border-border rounded-md bg-background text-foreground text-sm"
            >
              <option value="all">All Activities</option>
              <option value="upload">Uploads</option>
              <option value="view">Views</option>
              <option value="download">Downloads</option>
              <option value="share">Shares</option>
              <option value="edit">Edits</option>
              <option value="comment">Comments</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-foreground">Period:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-3 py-1 border border-border rounded-md bg-background text-foreground text-sm"
            >
              <option value="7days">Last 7 days</option>
              <option value="30days">Last 30 days</option>
              <option value="90days">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>
          
          <Input
            type="search"
            placeholder="Search activities..."
            className="w-64"
          />
        </div>
      </div>
      {/* Audit Events */}
      <div className="max-h-96 overflow-y-auto">
        {filteredEvents?.map((event, index) => {
          const { date, time } = formatTimestamp(event.timestamp);
          
          return (
            <div key={event.id} className="p-4 border-b border-border last:border-b-0 hover:bg-muted/30 transition-legal-fast">
              <div className="flex items-start space-x-4">
                {/* Event Icon */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-muted ${getEventColor(event.type)}`}>
                  <Icon name={getEventIcon(event.type)} size={16} />
                </div>
                
                {/* Event Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-foreground">{event.action}</h4>
                    <div className="text-xs text-muted-foreground">
                      {date} at {time}
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-2">{event.details}</p>
                  
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="User" size={12} />
                      <span>{event.user}</span>
                      <span className="text-muted-foreground/70">({event.userRole})</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Globe" size={12} />
                      <span>{event.ipAddress}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Monitor" size={12} />
                      <span>{event.userAgent?.split(' ')?.[0]}</span>
                    </div>
                  </div>
                </div>
                
                {/* Timeline Connector */}
                {index < filteredEvents?.length - 1 && (
                  <div className="absolute left-8 mt-8 w-px h-4 bg-border"></div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Summary Stats */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-lg font-bold text-primary">24</p>
            <p className="text-xs text-muted-foreground">Total Events</p>
          </div>
          <div>
            <p className="text-lg font-bold text-success">8</p>
            <p className="text-xs text-muted-foreground">Unique Users</p>
          </div>
          <div>
            <p className="text-lg font-bold text-warning">12</p>
            <p className="text-xs text-muted-foreground">Downloads</p>
          </div>
          <div>
            <p className="text-lg font-bold text-accent">156</p>
            <p className="text-xs text-muted-foreground">Total Views</p>
          </div>
        </div>
      </div>
      {/* Empty State */}
      {filteredEvents?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Activity" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No audit events found</h3>
          <p className="text-muted-foreground">Try adjusting your filters to see more results</p>
        </div>
      )}
    </div>
  );
};

export default AuditTrail;